import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../config';
import StepUpload from '../components/ImportWizard/StepUpload';
import StepMappatura from '../components/ImportWizard/StepMappatura';
import StepAnteprima from '../components/ImportWizard/StepAnteprima';
import StepConferma from '../components/ImportWizard/StepConferma';
import StepListaAttesa from '../components/ImportWizard/StepListaAttesa';
import { IconCheckCircle, IconCheck, IconSpinner, IconTrash } from '../components/Icons';

const STEPS = [
  { label: 'Carica' },
  { label: 'Mappatura' },
  { label: 'Anteprima' },
  { label: 'Conferma' },
];

function StepIndicator({ current }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 24 }}>
      {STEPS.map((s, i) => {
        const num = i + 1;
        const done = num < current;
        const active = num === current;
        return (
          <React.Fragment key={num}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 72 }}>
              <div style={{
                width: 36, height: 36,
                borderRadius: '50%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 14,
                background: done ? 'var(--ok)' : active ? 'var(--info)' : 'var(--line)',
                color: (done || active) ? '#fff' : 'var(--faint)',
                transition: 'all 0.2s',
              }}>
                {done ? <IconCheck size={14} /> : num}
              </div>
              <div style={{
                marginTop: 6, fontSize: 11, fontWeight: 600,
                color: active ? 'var(--info)' : done ? 'var(--ok)' : 'var(--faint)',
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {s.label}
              </div>
            </div>
            {i < STEPS.length - 1 && (
              <div style={{
                flex: 1, height: 2, marginBottom: 20,
                background: done ? 'var(--ok)' : 'var(--line)',
                transition: 'background 0.3s',
              }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// Storico importazioni: elenco dei lotti (per import_batch_id) con eliminazione.
function StoricoImport({ refreshKey }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [target, setTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/import/lotti`)
      .then(r => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const doDelete = async () => {
    if (!target) return;
    setDeleting(true);
    try {
      const r = await axios.delete(`${API}/import/lotti/${target.import_batch_id}`);
      toast.success(`${r.data.deleted} numeri eliminati`);
      setTarget(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Errore eliminazione'); }
    setDeleting(false);
  };

  const n = (v) => (v || 0).toLocaleString('it-IT');

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <h2 style={{ marginTop: 0 }}>Storico importazioni</h2>
      {loading && items.length === 0 ? (
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>Caricamento…</div>
      ) : items.length === 0 ? (
        <div style={{ color: 'var(--faint)', fontSize: 13 }}>Nessuna importazione registrata.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                <th>File</th><th>Data</th><th>Numeri</th><th>Verificati</th><th>Esportati</th><th></th>
              </tr>
            </thead>
            <tbody>
              {items.map(it => (
                <tr key={it.import_batch_id} style={{ borderTop: '1px solid var(--surface-2)' }}>
                  <td style={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.source_filename}>{it.source_filename}</td>
                  <td>{it.imported_at ? new Date(it.imported_at).toLocaleString('it-IT') : '—'}</td>
                  <td>{n(it.count)}</td>
                  <td>{n(it.verificati)}</td>
                  <td style={{ color: it.esportati > 0 ? 'var(--warn)' : undefined, fontWeight: it.esportati > 0 ? 700 : 400 }}>{n(it.esportati)}</td>
                  <td style={{ textAlign: 'right' }}>
                    {it.can_delete ? (
                      <button className="btn btn-secondary" style={{ color: 'var(--err)', padding: '4px 10px' }} onClick={() => setTarget(it)}>
                        <IconTrash size={14} /> Elimina
                      </button>
                    ) : (
                      <span style={{ color: 'var(--line)', fontSize: 12 }} title="Import non tuo">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {target && (
        <div className="nt-overlay" onClick={() => !deleting && setTarget(null)}>
          <div className="nt-detail" onClick={e => e.stopPropagation()} style={{ maxWidth: 470 }}>
            <div className="nt-detail-head">
              <span>Elimina importazione</span>
              <button className="ob-x" onClick={() => !deleting && setTarget(null)}>✕</button>
            </div>
            <div className="nt-detail-body">
              <p style={{ fontSize: 14, marginTop: 0 }}>
                Eliminare l'import <b>«{target.source_filename}»</b>?
              </p>
              <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6 }}>
                Verranno eliminati <b>{n(target.count)}</b> numeri, di cui <b>{n(target.verificati)}</b> già verificati
                {target.esportati > 0 && <> e <b style={{ color: 'var(--warn)' }}>{n(target.esportati)} già esportati</b></>}.
                <br />Operazione <b>irreversibile</b>.
              </p>
              <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
                <button className="btn btn-secondary" style={{ flex: 1 }} disabled={deleting} onClick={() => setTarget(null)}>Annulla</button>
                <button className="btn" style={{ flex: 1, background: 'var(--err)', color: '#fff', border: 'none' }} disabled={deleting} onClick={doDelete}>
                  {deleting ? <IconSpinner size={14} /> : null} Elimina definitivamente
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* «mancano circa 12 minuti»: su un import da 160.000 numeri la percentuale da sola non dice
   se sono minuti o ore. Arrotondato per eccesso, perche' una stima ottimista fa chiudere la
   pagina a meta' lavoro. */
export function quantoManca(secondi) {
  const s = Math.max(0, Math.round(secondi || 0));
  if (s < 60) return 'meno di un minuto';
  const min = Math.ceil(s / 60);
  if (min < 60) return `${min} ${min === 1 ? 'minuto' : 'minuti'}`;
  const ore = Math.floor(min / 60);
  const resto = min % 60;
  return `${ore} ${ore === 1 ? 'ora' : 'ore'}${resto ? ` e ${resto} min` : ''}`;
}

export default function ImportPage() {
  const [step, setStep] = useState(1);
  const [importDone, setImportDone] = useState(0);
  const [uploadData, setUploadData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [zeroTroncato, setZeroTroncato] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [result, setResult] = useState(null);
  // esito di una lista TIM/CERVED mandata in «Da qualificare» (non passa per l'archivio)
  const [listaAttesa, setListaAttesa] = useState(null);
  const [loading, setLoading] = useState(false);
  // Avanzamento dell'import: il server lavora di fondo e noi lo guardiamo.
  // Prima la pagina restava appesa alla risposta e dopo cinque minuti nginx
  // chiudeva la connessione: si vedeva un errore mentre l'import andava a buon
  // fine (10.848 numeri di Palmieri, 13/10/2026).
  const [avanzamento, setAvanzamento] = useState(null);
  const [autoBulk, setAutoBulk] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [error, setError] = useState(null);

  // Step 1 → 2: file uploaded
  function handleUploaded(data) {
    setUploadData(data);
    // Lista TIM o CERVED: l'ha riconosciuta il server dall'intestazione. Niente mappatura
    // delle colonne — quel file non porta numeri da archiviare, porta aziende da ritrovare.
    if (data.lista_in_attesa) { setStep(2); return; }
    // Use auto-mapping from backend, fall back to empty map
    const initMapping = {};
    (data.headers || []).forEach(h => {
      initMapping[h] = (data.mapping && data.mapping[h]) || '';
    });
    setMapping(initMapping);
    setStep(2);
  }

  // Step 2 → 3: confirm mapping, fetch preview
  async function handleMappingConfirm() {
    setError(null);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/import/preview`, {
        upload_id: uploadData.upload_id,
        mapping,
        zero_troncato: zeroTroncato,
      });
      setPreviewData(res.data);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore durante la generazione dell\'anteprima');
    } finally {
      setLoading(false);
    }
  }

  // Chiede al server a che punto e' l'import, finche' non ha finito.
  // Un errore di rete passeggero non ferma il seguito: si riprova al giro dopo, perche'
  // il lavoro sta andando avanti comunque e chi guarda non deve vedere un falso allarme.
  async function seguiImport(jobId) {
    let errori = 0;
    for (;;) {
      await new Promise(r => setTimeout(r, 700));
      try {
        const r = await axios.get(`${API}/import/confirm/${jobId}`);
        setAvanzamento(r.data);
        if (r.data.stato !== 'in_corso') return r.data;
        errori = 0;
      } catch (e) {
        if (++errori > 20) return { stato: 'errore', errore: "Non riesco a seguire l'importazione" };
      }
    }
  }

  // Step 3 → 4: confirm import
  async function handleImportConfirm() {
    setError(null);
    setLoading(true);
    try {
      const avvio = await axios.post(`${API}/import/confirm`, {
        upload_id: uploadData.upload_id,
        skip_duplicates: skipDuplicates,
      });
      // Da qui in poi si guarda il lavoro, non si aspetta piu' niente.
      setLoading(false);
      setAvanzamento({ fatti: 0, totale: avvio.data.totale, percentuale: 0 });
      const esito = await seguiImport(avvio.data.job_id);
      setAvanzamento(null);
      if (esito.stato === 'errore') {
        setError(esito.errore || "Errore durante l'importazione");
        return;
      }
      const res = { data: esito };
      setResult(res.data);
      setStep(4);
      setImportDone(d => d + 1);
      // Avvio automatico verifica del batch importato (gestisce agente/coda lato server)
      if (autoBulk && res.data.import_batch_id) {
        try {
          const b = await axios.post(`${API}/verify-bulk/after-import`, { import_batch_id: res.data.import_batch_id });
          if (b.data.avviato) {
            toast.success(`Verifica automatica avviata su ${b.data.total} numeri`);
          } else {
            toast.info(b.data.messaggio || 'Verifica non avviata');
          }
        } catch (be) {
          toast.error(be?.response?.data?.detail || 'Errore avvio verifica automatica');
        }
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Errore durante l\'importazione');
    } finally {
      setLoading(false);
    }
  }

  // Step 4 → 1: reset
  function handleReset() {
    setStep(1);
    setUploadData(null);
    setMapping({});
    setZeroTroncato(false);
    setPreviewData(null);
    setResult(null);
    setListaAttesa(null);
    setError(null);
    setLoading(false);
  }

  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <h1 style={{ margin: '0 0 4px', fontSize: 22, fontWeight: 800, color: 'var(--text)' }}>
          Importazione Numeri
        </h1>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--muted)' }}>
          Carica un file Excel o CSV e segui i passaggi per importare i contatti.
        </p>
      </div>

      {/* I quattro passaggi valgono per una lista di numeri. Una lista TIM/CERVED non li fa:
          niente mappatura, niente anteprima — si conferma e basta. */}
      {!uploadData?.lista_in_attesa && !listaAttesa && (
        <div className="card" style={{ marginBottom: 20 }}>
          <StepIndicator current={step} />
        </div>
      )}

      {error && (
        <div style={{
          marginBottom: 16, padding: '12px 16px',
          background: '#fef2f2', border: '1px solid var(--err)',
          borderRadius: 8, color: 'var(--err)', fontSize: 13
        }}>
          {error}
        </div>
      )}

      {avanzamento && (
        <div className="card" style={{ padding: 28 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <strong>Importazione in corso…</strong>
            <span style={{ color: 'var(--muted)', fontSize: 13 }}>
              {(avanzamento.fatti || 0).toLocaleString('it-IT')} di {(avanzamento.totale || 0).toLocaleString('it-IT')} numeri
            </span>
          </div>
          <div style={{ height: 10, background: 'var(--surface-2)', borderRadius: 6, overflow: 'hidden' }}>
            <div style={{ width: `${avanzamento.percentuale || 0}%`, height: '100%',
                          background: 'linear-gradient(90deg, #33b0e8, #1f7fc4)',
                          transition: 'width .4s ease' }} />
          </div>
          {/* Cosa sta facendo, e i conti che scorrono: senza, una barra che avanza non
              dice se sta scrivendo, controllando o finendo. */}
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 600 }}>{avanzamento.descrizione || 'Importazione in corso'}</span>
            <span style={{ fontSize: 13, color: 'var(--muted)' }}>
              {(avanzamento.inseriti || 0).toLocaleString('it-IT')} nuovi
              {' · '}
              {((avanzamento.duplicati || 0) + (avanzamento.gia_presenti || 0)).toLocaleString('it-IT')} già presenti
              {avanzamento.mancano_secondi > 0 && <> · mancano circa {quantoManca(avanzamento.mancano_secondi)}</>}
            </span>
          </div>
          <p style={{ marginTop: 8, color: 'var(--muted)', fontSize: 13 }}>
            Puoi restare su questa pagina: il lavoro prosegue sul server anche se la chiudi.
          </p>
        </div>
      )}

      {loading && !avanzamento && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <IconSpinner size={36} />
          <p style={{ marginTop: 12, color: 'var(--muted)' }}>Elaborazione in corso…</p>
        </div>
      )}

      {!loading && step === 1 && (
        <StepUpload onUploaded={handleUploaded} />
      )}

      {listaAttesa && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
            <IconCheckCircle size={56} color="#06b6d4" />
          </div>
          <h2 style={{ textAlign: 'center', textTransform: 'none', letterSpacing: 0,
                       color: '#0891b2' }}>
            Lista {listaAttesa.tipo === 'cerved' ? 'CERVED' : 'TIM'} in «Da qualificare»
          </h2>
          <p style={{ textAlign: 'center', color: 'var(--muted)', marginTop: 0 }}>
            <strong>{(listaAttesa.da_indagare || 0).toLocaleString('it-IT')}</strong>
            {(listaAttesa.da_indagare === 1 ? ' riga pronta' : ' righe pronte')} da indagare
            {listaAttesa.scartate > 0 && <> · {listaAttesa.scartate.toLocaleString('it-IT')} scartate
              all’ingresso (cellulari, persone fisiche, aziende già in archivio)</>}
            {listaAttesa.saltate > 0 && <> · {listaAttesa.saltate.toLocaleString('it-IT')} {listaAttesa.saltate === 1 ? 'riga illeggibile' : 'righe illeggibili'}</>}
          </p>
          <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--muted)' }}>
            Le righe sono <strong>spente</strong>: nessuna indagine parte finché non le accendi
            tu da «Da qualificare».
          </p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
            <button className="btn" onClick={handleReset}>Carica un altro file</button>
            <a className="btn btn-primary" href="/campagne" style={{ textDecoration: 'none' }}>
              Vai a Da qualificare
            </a>
          </div>
        </div>
      )}

      {!loading && !listaAttesa && step === 2 && uploadData?.lista_in_attesa && (
        <StepListaAttesa
          dati={uploadData.lista_in_attesa}
          uploadId={uploadData.upload_id}
          nomeFile={uploadData.filename || 'il file'}
          onIndietro={handleReset}
          onFatto={(esito) => { setListaAttesa(esito); setImportDone(d => d + 1); }}
        />
      )}

      {!loading && step === 2 && uploadData && !uploadData.lista_in_attesa && (
        <StepMappatura
          headers={uploadData.headers || []}
          mapping={mapping}
          preview={uploadData.preview_rows || uploadData.preview || []}
          zeroTroncato={zeroTroncato}
          campi={uploadData.campi}
          onMappingChange={setMapping}
          onZeroTroncatoChange={setZeroTroncato}
          onConfirm={handleMappingConfirm}
          onBack={() => setStep(1)}
        />
      )}

      {!loading && !avanzamento && step === 3 && previewData && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <h2>Opzioni import</h2>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', flex: '1 1 280px',
                background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 10,
                cursor: 'pointer', fontSize: 14,
              }}>
                <input type="checkbox" checked={skipDuplicates} onChange={e => setSkipDuplicates(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                <span>
                  <strong>Non importare numeri duplicati</strong>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>
                    I numeri già presenti (confronto sul numero pulito) vengono saltati
                  </span>
                </span>
              </label>
              <label style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', flex: '1 1 280px',
                background: 'var(--bg-card)', border: '1px solid var(--border-card)', borderRadius: 10,
                cursor: 'pointer', fontSize: 14,
              }}>
                <input type="checkbox" checked={autoBulk} onChange={e => setAutoBulk(e.target.checked)}
                  style={{ width: 18, height: 18, accentColor: 'var(--accent)', cursor: 'pointer' }} />
                <span>
                  <strong>Avvia subito la lavorazione 456</strong>
                  <span style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary)' }}>
                    Al termine dell'import parte automaticamente il bulk su questa lista
                  </span>
                </span>
              </label>
            </div>
          </div>
          <StepAnteprima
            previewData={previewData}
            onConfirm={handleImportConfirm}
            onBack={() => setStep(2)}
          />
        </>
      )}

      {!loading && step === 4 && result && (
        <StepConferma
          result={result}
          onReset={handleReset}
        />
      )}

      <StoricoImport refreshKey={importDone} />
    </div>
  );
}
